"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { PageSection, PageSectionHeader, PageGridItem } from '../structure/page-blocks';
import { Callout } from '../structure/callout';
import { contentfulValueToSlug } from '../integrations/contentful.delivery';
import { usePixelatedConfig } from '../config/config.client';
import { renderArrayToParagraphs } from '../foundation/schema';
const defaultServiceAreaPathPrefix = '/service-areas';
export function buildServiceAreaUrl(item, prefix = defaultServiceAreaPathPrefix) {
    const slug = contentfulValueToSlug({ value: item.name });
    return slug ? `${prefix}/${slug}` : prefix;
}
/**
 * ServiceAreas propTypes define the list data shape for rendering a grid of service area cards.
 * The component uses the config provider's `siteInfo.serviceAreas` data internally.
 */
ServiceAreas.propTypes = {
    /** Section title to display above the list. */
    title: PropTypes.string,
    /** Introductory text shown under the title. */
    intro: PropTypes.string,
    /** Optional URL prefix for service area cards. */
    serviceAreaPathPrefix: PropTypes.string,
    /** HTML id attribute for the list section. */
    id: PropTypes.string,
};
export function ServiceAreas({ title = 'Service Areas', intro, serviceAreaPathPrefix = defaultServiceAreaPathPrefix, id }) {
    const config = usePixelatedConfig();
    const siteInfo = config?.siteInfo;
    const items = siteInfo?.serviceAreas || [];
    if (!items?.length) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs(PageSection, { id: `${id}-header`, columns: 1, children: [_jsx(PageSectionHeader, { title: title ?? 'Service Areas' }), intro ? _jsx("p", { children: intro }) : null] }), _jsx(PageSection, { id: id, className: "service-areas-list", layoutType: "grid", gap: "24px", columns: 3, children: items.map((serviceArea, index) => (_jsx(PageGridItem, { children: _jsx(ServiceAreaCard, { serviceArea: serviceArea, serviceAreaPathPrefix: serviceAreaPathPrefix }, index) }, index))) })] }));
}
/**
 * ServiceAreaCard propTypes define the incoming service area data shape used to render the card.
 *
 * @param {ServiceAreaCardType} props - The properties for the ServiceAreaCard component, including the service area data and an optional URL prefix for links.
 * @return A Callout component representing the service area, with a title, description, highlights, related services, and a link to learn more.
 */
ServiceAreaCard.propTypes = {
    /** Service area object to render in the card. */
    serviceArea: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.arrayOf(PropTypes.string).isRequired,
        short_description: PropTypes.string,
        keywords: PropTypes.arrayOf(PropTypes.string),
        highlights: PropTypes.arrayOf(PropTypes.string),
        relatedServices: PropTypes.arrayOf(PropTypes.string),
        image: PropTypes.string,
    }).isRequired,
    /** Optional URL prefix for service area links. */
    serviceAreaPathPrefix: PropTypes.string,
};
export function ServiceAreaCard({ serviceArea, serviceAreaPathPrefix = defaultServiceAreaPathPrefix }) {
    const url = buildServiceAreaUrl(serviceArea, serviceAreaPathPrefix ?? defaultServiceAreaPathPrefix);
    const keywords = serviceArea.keywords ? serviceArea.keywords.join(', ') : undefined;
    return (_jsx(Callout, { variant: "boxed", layout: "vertical", direction: "left", title: serviceArea.name, content: serviceArea.short_description, url: url, img: serviceArea.image, imgAlt: serviceArea.name, buttonText: `View ${serviceArea.name}` }));
}
/**
 * ServiceAreaDetail propTypes define the incoming service area detail lookup data shape.
 * The component resolves the active service area from `siteInfo.serviceAreas` in the config provider.
 *
 * @param {ServiceAreaDetailType} props - The properties for the ServiceAreaDetail component, including the slug used to resolve the service area, title override, URL prefix, and display options.
 * @return A detailed page section for the active service area, including the name, description, highlights, related services, and a link to the service area page.
 */
ServiceAreaDetail.propTypes = {
    /** Slug used to resolve the active service area from config. */
    serviceAreaSlug: PropTypes.string,
    /** Page title override. */
    title: PropTypes.string,
    /** Optional URL prefix for service area detail links. */
    serviceAreaPathPrefix: PropTypes.string,
    /** HTML id attribute for the detail section. */
    id: PropTypes.string,
};
export function ServiceAreaDetail({ serviceAreaSlug, title, serviceAreaPathPrefix = defaultServiceAreaPathPrefix, id }) {
    const config = usePixelatedConfig();
    const siteInfo = config?.siteInfo;
    const activeArea = (siteInfo?.serviceAreas || []).find((item) => {
        const itemSlug = contentfulValueToSlug({ value: item.name });
        return itemSlug === (serviceAreaSlug ?? '');
    }) ?? undefined;
    if (!activeArea) {
        return null;
    }
    const url = buildServiceAreaUrl(activeArea, serviceAreaPathPrefix ?? defaultServiceAreaPathPrefix);
    const services = (siteInfo?.services ?? []);
    const serviceLinks = services.map((service) => {
        const href = `/services/${contentfulValueToSlug({ value: service.name })}`;
        return { name: service.name, href };
    });
    return (_jsxs(PageSection, { id: id, className: "serviceareadetailpage", layoutType: "none", gap: "20px", children: [_jsx(PageSectionHeader, { title: title ?? activeArea.name }), _jsxs("div", { className: "service-area-detail-copy", children: [renderArrayToParagraphs(activeArea.description), activeArea.highlights ? (_jsxs(_Fragment, { children: [_jsx("h4", { children: "Highlights" }), _jsx("ul", { children: activeArea.highlights.map((highlight, idx) => (_jsx("li", { children: highlight }, idx))) })] })) : null, _jsx("br", {}), serviceLinks.length ? (_jsxs("div", { children: [_jsx("p", { children: _jsxs("strong", { children: ["Services offered in ", activeArea.name, ":"] }) }), _jsx("ul", { children: serviceLinks.map((service, idx) => (_jsx("li", { children: _jsxs("a", { href: service.href, children: [service.name, " in ", activeArea.name] }) }, idx))) })] })) : null] })] }));
}
